import { createContext, useState, useEffect, useCallback, ReactNode } from "react";

export interface CartItem {
  id: string;
  type: "box" | "subscription" | "ai_credits";
  name: string;
  price: number;
  quantity: number;
  interval?: string; // "month" | "year" for subscriptions
  metadata: Record<string, any>; // price_id, product_id, age_group, skill_level, etc.
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  total: number;
  isDrawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
}

export const CartContext = createContext<CartContextType | null>(null);

const CART_KEY = "castready_cart";

const loadCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((newItem: Omit<CartItem, "quantity"> & { quantity?: number }) => {
    setItems((prev) => {
      // For subscriptions/credits, prevent duplicates
      if (newItem.type === "subscription" || newItem.type === "ai_credits") {
        const existing = prev.find((i) => i.id === newItem.id);
        if (existing) return prev; // already in cart
      }

      const existing = prev.find((i) => i.id === newItem.id);
      if (existing) {
        // For boxes, increment quantity
        return prev.map((i) =>
          i.id === newItem.id
            ? { ...i, quantity: i.quantity + (newItem.quantity || 1) }
            : i
        );
      }

      return [...prev, { ...newItem, quantity: newItem.quantity || 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i))
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        itemCount,
        total,
        isDrawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
